import AttachmentResource from '@/api/resources/attachment/attachmentResource'
import attachmentTypeService from './attachmentTypeService'
import { auth } from '@/api/auth'
import { BASE_URL, VIEWER_BASE_URL } from '@/api/api'
import { Base64 } from 'js-base64'

class SubportionAttachmentService extends AttachmentResource {
    async addAttachment(labId, caseId, sampleId, portionId, subportionId, attachmentTypeId, file, thumbnailBytes) {
        let thumbnail

        if (thumbnailBytes) {
            thumbnail = this.base64ToBlob(thumbnailBytes, file.name)
        } else {
            let generatedThumbnailBytes = await this.thumbnailifyFromBlob(file)
            thumbnail = this.base64ToBlob(generatedThumbnailBytes, file.name)
        }

        const formData = new FormData()
        formData.append('file', file, file.name)
        formData.append('atyId', attachmentTypeId)

        if (thumbnail) {
            formData.append('thumbnail', thumbnail, file.name)
        }

        const response = await this.axios.post(
            `/common/laboratories/${labId}/cases/${caseId}/samples/${sampleId}/portions/${portionId}/subportions/${subportionId}/attachments`,
            formData, { headers: { 'content-type': 'multipart/form-data' } })
        return response.data
    }

    async getAttachments(laboratoryId, caseId, sampleId, portionId, subportionId, attachmentTypeId) {
        const params = { attachmentTypeId }
        const response = await this.axios.get(`/common/laboratories/${laboratoryId}/cases/${caseId}/samples/${sampleId}/portions/${portionId}/subportions/${subportionId}/attachments`, { params })
        return response.data
    }

    async getFile(laboratoryId, caseId, sampleId, portionId, subportionId, attachmentId) {
        return this.axios.get(`/common/laboratories/${laboratoryId}/cases/${caseId}/samples/${sampleId}/portions/${portionId}/subportions/${subportionId}/attachments/${attachmentId}`, {
            headers: { 'content-type': 'multipart/form-data' },
            responseType: 'blob'
        }).then(response => response.data)
    }

    async patchAttachment(laboratoryId, caseId, sampleId, portionId, subportionId, attachmentId, request) {
        const response = await this.axios.patch(`/common/laboratories/${laboratoryId}/cases/${caseId}/samples/${sampleId}/portions/${portionId}/subportions/${subportionId}/attachments/${attachmentId}`, request)
        return response.data
    }

    getUrlFile(laboratoryId, caseId, sampleId, portionId, subportionId, attachmentId) {
        return `${BASE_URL}/common/laboratories/${laboratoryId}/cases/${caseId}/samples/${sampleId}/portions/${portionId}/subportions/${subportionId}/attachments/${attachmentId}?authenticationToken=${auth.token}`
    }

    async deleteAttachment(laboratoryId, caseId, sampleId, portionId, subportionId, attachmentId) {
        const response = await this.axios.delete(`/common/laboratories/${laboratoryId}/cases/${caseId}/samples/${sampleId}/portions/${portionId}/subportions/${subportionId}/attachments/${attachmentId}`)
        return response.data
    }

    getUrlForAttachment(laboratoryId, caseId, sampleId, portionId, subportionId, attachmentId, originalAttachment = false) {
        return `${BASE_URL}/common/laboratories/${laboratoryId}/cases/${caseId}/samples/${sampleId}/portions/${portionId}/subportions/${subportionId}/attachments/${attachmentId}?originalAttachment=${originalAttachment}&authenticationToken=${auth.token}`
    }

    getUrlForAttachmentFiltered(laboratoryId, caseId, sampleId, portionId, subportionId, attachmentId) {
        return `${VIEWER_BASE_URL}/laboratories/${laboratoryId}/cases/${caseId}/samples/${sampleId}/portions/${portionId}/subportions/${subportionId}/attachments/${attachmentId}?authenticationToken=${auth.token}`
    }

    getUrlForWMTSAttachment(laboratoryId, caseId, sampleId, portionId, subportionId, attachmentId) {
        return `${VIEWER_BASE_URL}/laboratories/${laboratoryId}/cases/${caseId}/samples/${sampleId}/portions/${portionId}/subportions/${subportionId}/attachments/${attachmentId}/wmts`
    }

    getUrlForAttachmentThumbnail(laboratoryId, caseId, sampleId, portionId, subportionId, attachmentId) {
        return `${BASE_URL}/common/laboratories/${laboratoryId}/cases/${caseId}/samples/${sampleId}/portions/${portionId}/subportions/${subportionId}/attachments/${attachmentId}?authenticationToken=${auth.token}`
    }

    getUrlForWMTSAttachmentThumbnail(laboratoryId, caseId, sampleId, portionId, subportionId, attachmentId) {
        return `${BASE_URL}/viewer/laboratories/${laboratoryId}/cases/${caseId}/samples/${sampleId}/portions/${portionId}/subportions/${subportionId}/attachments/${attachmentId}/wmts/thumbnail`
    }

    async loadSubportionAnnotations(laboratoryId, caseId, sampleId, portionId, subportionId, attachmentId) {
        return this.axios.get(`/common/laboratories/${laboratoryId}/cases/${caseId}/samples/${sampleId}/portions/${portionId}/subportions/${subportionId}/attachments/${attachmentId}/annotations`)
            .then(response => response.data)
    }

    async createSubportionAnnotations(laboratoryId, caseId, sampleId, portionId, subportionId, attachmentId, annotations, imageBytes, thumbnailBytes, attachmentFilename) {
        let annotationsBlob = new Blob([annotations], { 'type': 'application/json' })
        let thumbnail
        let image

        if (thumbnailBytes) {
            thumbnail = this.base64ToBlob(thumbnailBytes, 'thumbnail')
        }
        if (imageBytes) {
            image = this.base64ToBlob(imageBytes, 'image')
        }

        const formData = new FormData()
        formData.append('annotationFile', annotationsBlob, 'thumbnail')

        if (thumbnail) {
            formData.append('thumbnail', thumbnail)
        }
        if (image) {
            formData.append('image', image, attachmentFilename)
        }

        const params = { attachmentFilename }

        return this.axios.post(`/common/laboratories/${laboratoryId}/cases/${caseId}/samples/${sampleId}/portions/${portionId}/subportions/${subportionId}/attachments/${attachmentId}/annotations`,
            formData, { params, headers: { 'content-type': 'multipart/form-data' } })
            .then(response => response.data)
    }

    async updateSubportionAnnotations(laboratoryId, caseId, sampleId, portionId, subportionId, attachmentId, annotationId, annotations, imageBytes, thumbnailBytes, attachmentFilename) {
        let annotationsBlob = new Blob([annotations], { 'type': 'application/json' })
        let thumbnail
        let image

        if (thumbnailBytes) {
            thumbnail = this.base64ToBlob(thumbnailBytes, 'thumbnail')
        }
        if (imageBytes) {
            image = this.base64ToBlob(imageBytes, 'image')
        }

        const formData = new FormData()
        formData.append('annotationFile', annotationsBlob, 'thumbnail')

        if (thumbnail) {
            formData.append('thumbnail', thumbnail)
        }
        if (image) {
            formData.append('image', image, attachmentFilename)
        }

        const params = { attachmentFilename }

        return this.axios.put(`/common/laboratories/${laboratoryId}/cases/${caseId}/samples/${sampleId}/portions/${portionId}/subportions/${subportionId}/attachments/${attachmentId}/annotations/${annotationId}`,
            formData, { params, headers: { 'content-type': 'multipart/form-data' } })
            .then(response => response.data)
    }

    async getLastScannedImageAttachment(laboratoryId, caseId, sampleId, portionId, subportionId) {
        return this.axios.get(`/common/laboratories/${laboratoryId}/cases/${caseId}/samples/${sampleId}/portions/${portionId}/subportions/${subportionId}/lastScannedImageAttachment`)
            .then(response => response.data)
    }

    async getUrlForOpenViewer(laboratoryId, caseId, sampleId, portionId, subportionId, attachmentId) {
        return this.axios.get(`/externalviewer/laboratories/${laboratoryId}/cases/${caseId}/samples/${sampleId}/portions/${portionId}/subportions/${subportionId}/attachments/${attachmentId}/url`, {
        }).then(response => Base64.decode(response.data))
    }

    async getAttachment(laboratoryId, caseId, sampleId, portionId, subportionId, attachmentId) {
        const response = await this.axios.get(`/common/laboratories/${laboratoryId}/cases/${caseId}/samples/${sampleId}/portions/${portionId}/subportions/${subportionId}/attachments/${attachmentId}/details`)
        return response.data
    }

    async getWSIAttachmentDetails(laboratoryId, caseId, sampleId, portionId, subportionId, attachmentId) {
        const response = await this.axios.get(`${VIEWER_BASE_URL}/laboratories/${laboratoryId}/cases/${caseId}/samples/${sampleId}/portions/${portionId}/subportions/${subportionId}/attachments/${attachmentId}/details`)
        return response.data
    }

    async getWSIFileInfo(laboratoryId, caseId, sampleId, portionId, subportionId, attachmentId) {
        const response = await this.axios.get(`${VIEWER_BASE_URL}/laboratories/${laboratoryId}/cases/${caseId}/samples/${sampleId}/portions/${portionId}/subportions/${subportionId}/attachments/${attachmentId}/header`)
        return response.data
    }

    async getAllMacroImageAttachments(laboratoryId, caseId, sampleId, portionId, subportionId) {
        const attachmentType = await attachmentTypeService.getAttachmentType('SUBPORTION', 'attachment.type.microscopicimage.code')
        return this.getAttachmentsForAttachmentType(laboratoryId, caseId, sampleId, portionId, subportionId, attachmentType.atyId)
    }

    async getAttachmentsForAttachmentType(laboratoryId, caseId, sampleId, portionId, subportionId, attachmentTypeId) {
        const params = { attachmentTypeId }
        return this.axios.get(`/common/laboratories/${laboratoryId}/cases/${caseId}/samples/${sampleId}/portions/${portionId}/subportions/${subportionId}/attachments`, { params }).then(response => response.data)
    }

    async updateMeasureScale(laboratoryId, caseId, sampleId, portionId, subportionId, attachmentId, scale) {
        return this.patchAttachment(laboratoryId, caseId, sampleId, portionId, subportionId, attachmentId, { scale })
    }
}

export default new SubportionAttachmentService()
